import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Typography, TextField, InputAdornment, Skeleton, Button, Tabs, Tab,
  Table, TableHead, TableRow, TableCell, TableBody, useTheme, useMediaQuery, alpha,
  Card, CardContent, Pagination, Snackbar, Alert, Stack, Chip, IconButton, Tooltip,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import { useGetMyAssessmentsQuery } from "../../Api/attoDeskApi";
import useUser from "../../Core/hook/useUser";
import StaffAssessmentDialog from "./components/StaffAssessmentDialog";

/**
 * Staff Portal → My Assessments.
 *
 * Visually mirrors the ADMIN Assessments page (navy hero, tinted KPI
 * strip with top-border accent, navy table header, admin-style empty
 * state + primary CTA) so both surfaces feel like the same product
 * family. Data-wise this page is strictly staff-scoped — hits only
 * `/api/staff-portal/assessments`, whose row filter is
 * StaffTeachingScopeService::canAccessAssessment.
 *
 * The admin page (`Pages/Assessments/index.tsx`) and admin wizard
 * (`Pages/Assessments/CreateAssessmentWizard.tsx`) are NOT imported
 * from here — visual parity was achieved by re-implementing the
 * grammar with MUI + the same palette (`#1b3a6b` → `#2a5298`).
 */

// Palette shared with the admin Assessments page and the create dialog.
const NAVY = "#1b3a6b";
const NAVY_2 = "#2a5298";
const PAGE_BG = "#f0f4f8";

type Row = {
  assessment_id: number;
  title: string;
  subject: string | null;
  grade: string | null;
  assessment_date: string | null;
  score: number | null;
  max_score: number | null;
  percentage: number | null;
  marked: boolean;
  result_status: string | null;
  student: { id: number; name: string; grade: string | null } | null;
};

type TabKey = "" | "to_review" | "graded";

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
const initials = (name: string) =>
  name.split(/\s+/).map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
const percentTone = (r: number) =>
  r >= 80 ? { fg: "#16a34a", bg: "#dcfce7" } :
  r >= 50 ? { fg: "#d97706", bg: "#fef3c7" } :
            { fg: "#dc2626", bg: "#fee2e2" };

const Assessments: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate } = useUser();
  const canCreateAssessment = canCreate("Assessment");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tab, setTab] = useState<TabKey>("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 25;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading, isFetching, isError } = useGetMyAssessmentsQuery({
    status: tab || undefined, search: search || undefined, page, per_page: perPage,
  });
  const rows: Row[] = useMemo(() => (data as any)?.data?.rows ?? [], [data]);
  const total: number = (data as any)?.data?.total ?? 0;
  const counts = (data as any)?.data?.counts ?? { to_review: 0, graded: 0, upcoming: 0 };
  const pageCount = Math.max(1, Math.ceil(total / perPage));

  // Auto-open the dialog when the ?compose=1 URL param is present
  // (used by the `/staff-portal/assessments/new` legacy route +
  // Student Detail "Create assessment" deep links).
  useEffect(() => {
    if (searchParams.get("compose") === "1" && canCreateAssessment) {
      setDialogOpen(true);
    }
    if (searchParams.get("created") === "1") {
      setToast("Assessment created successfully.");
      const p = new URLSearchParams(searchParams);
      p.delete("created");
      setSearchParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDialog = () => {
    if (!canCreateAssessment) return;
    setDialogOpen(true);
    const p = new URLSearchParams(searchParams);
    p.set("compose", "1");
    setSearchParams(p, { replace: true });
  };
  const closeDialog = () => {
    setDialogOpen(false);
    const p = new URLSearchParams(searchParams);
    if (p.has("compose")) {
      p.delete("compose");
      setSearchParams(p, { replace: true });
    }
  };
  const onCreated = (msg: string) => {
    closeDialog();
    setToast(msg);
    // The mutation invalidates ["team","student"] tags which the list
    // provides, so RTK refetches automatically.
  };

  const openDetail = (id: number) => navigate(`/staff-portal/assessments/${id}`);

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "100vh", pb: 5 }}>
      {/* ── HERO — mirrors admin navy hero ────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_2} 100%)`,
          color: "#fff",
          px: { xs: 2.5, md: 4 },
          pt: { xs: 3, md: 3 },
          pb: { xs: 3, md: 3 },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "flex-start" }}
          spacing={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, letterSpacing: "0.8px", textTransform: "uppercase", fontWeight: 700, opacity: 0.75 }}>
              Staff Portal
            </Typography>
            <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, mt: 0.25, letterSpacing: "-0.01em" }}>
              Assessments
            </Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.75, mt: 0.5 }}>
              Assessments for students and classes assigned to you.
            </Typography>
          </Box>
          {canCreateAssessment && (
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={openDialog}
              aria-label="Create new assessment"
              sx={{
                bgcolor: "#ffffff",
                color: NAVY,
                fontWeight: 800,
                fontSize: 14,
                textTransform: "none",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.7)",
                px: 3,
                minHeight: 44,
                boxShadow: "0 6px 18px rgba(0,0,0,0.22)",
                alignSelf: { xs: "stretch", sm: "flex-start" },
                width: { xs: "100%", sm: "auto" },
                "&:hover": {
                  bgcolor: "#ffffff",
                  boxShadow: "0 8px 22px rgba(0,0,0,0.28)",
                  transform: "translateY(-1px)",
                },
                "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 },
              }}
            >
              New Assessment
            </Button>
          )}
        </Stack>
      </Box>

      {/* ── KPI ROW — 4 tinted cards with top-border accent ──────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 2.5 }}>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
          }}
        >
          <KpiCard label="Total" value={isLoading ? "—" : total} icon={<SummarizeOutlinedIcon />} accent={NAVY} bg="#eef2ff" />
          <KpiCard label="To Review" value={isLoading ? "—" : counts.to_review} icon={<HowToRegOutlinedIcon />} accent="#d97706" bg="#fffbeb" />
          <KpiCard label="Graded" value={isLoading ? "—" : counts.graded} icon={<CheckCircleOutlineIcon />} accent="#16a34a" bg="#f0fdf4" />
          <KpiCard label="Upcoming" value={isLoading ? "—" : counts.upcoming} icon={<HourglassEmptyOutlinedIcon />} accent="#7c3aed" bg="#f5f3ff" />
        </Box>
      </Box>

      {/* ── FILTER TOOLBAR + RESULTS ─────────────────────────────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 2.5 }}>
        <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden" }}>
          {/* Tabs styled inside the filter card — admin filter grammar */}
          <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 1, borderBottom: "1px solid #f1f5f9" }}>
            <Tabs
              value={tab}
              onChange={(_, v) => { setTab(v); setPage(1); }}
              sx={{
                minHeight: 40,
                "& .MuiTab-root": {
                  textTransform: "none", fontWeight: 700, fontSize: 13,
                  minHeight: 40, color: "#64748b",
                },
                "& .Mui-selected": { color: NAVY },
                "& .MuiTabs-indicator": { backgroundColor: NAVY, height: 3, borderRadius: "3px 3px 0 0" },
              }}
            >
              <Tab value="" label="All" />
              <Tab value="graded" label={`Graded${counts.graded ? ` (${counts.graded})` : ""}`} />
              <Tab value="to_review" label={`To Review${counts.to_review ? ` (${counts.to_review})` : ""}`} />
            </Tabs>
          </Box>

          {/* Search row */}
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <TextField
              fullWidth size="small"
              placeholder="Search assessment title…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment> }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Box>

          {/* Table */}
          {isLoading || isFetching ? (
            <Box sx={{ p: 2 }}>
              <Skeleton height={44} /><Skeleton height={44} sx={{ mt: 1 }} /><Skeleton height={44} sx={{ mt: 1 }} />
            </Box>
          ) : isError ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error" sx={{ fontWeight: 700 }}>Could not load assessments.</Typography>
            </Box>
          ) : rows.length === 0 ? (
            <EmptyState tab={tab} search={search} canCreate={canCreateAssessment} onCreate={openDialog} />
          ) : isMobile ? (
            // Mobile: card list (no horizontal scroll)
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1.5 }}>
              {rows.map((r) => (
                <Card key={r.assessment_id} onClick={() => openDetail(r.assessment_id)} sx={{ borderRadius: 2, cursor: "pointer", border: "1px solid #e2e8f0" }}>
                  <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <StudentAvatar name={r.student?.name ?? "—"} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{r.student?.name ?? "—"}</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{r.title}</Typography>
                      <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
                        {[r.student?.grade ?? r.grade, r.subject].filter(Boolean).join(" · ")} · {fmtDate(r.assessment_date)}
                      </Typography>
                    </Box>
                    {renderResult(r)}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            // Desktop: navy-header table (admin grammar)
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  {/* Header text is forced white via per-cell sx (with
                      !important) because the previous row-scoped sx tied
                      MUI's own .MuiTableCell-head selector on specificity
                      and the injected MUI style won on stylesheet order,
                      leaving the labels rendering dark. Setting color on
                      each cell instance and on any child element (`& *`)
                      guarantees the final resolved color is #ffffff. */}
                  {(() => {
                    const headCellSx = {
                      color: "#ffffff !important",
                      opacity: 1,
                      fontSize: 10.5,
                      fontWeight: 800,
                      letterSpacing: "0.6px",
                      textTransform: "uppercase" as const,
                      borderBottom: "none",
                      "& *": { color: "#ffffff !important" },
                      "& .MuiSvgIcon-root": { color: "#ffffff !important" },
                      "& .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root:focus, & .MuiTableSortLabel-root.Mui-active": {
                        color: "#ffffff !important",
                      },
                      "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                    };
                    return (
                      <TableRow sx={{ bgcolor: NAVY }}>
                        <TableCell sx={headCellSx}>Date</TableCell>
                        <TableCell sx={headCellSx}>Student</TableCell>
                        <TableCell sx={headCellSx}>Subject</TableCell>
                        <TableCell sx={headCellSx}>Title</TableCell>
                        <TableCell sx={headCellSx}>Grade</TableCell>
                        <TableCell sx={headCellSx}>Status</TableCell>
                        <TableCell sx={headCellSx}>Marks</TableCell>
                        <TableCell align="right" sx={headCellSx}>Actions</TableCell>
                      </TableRow>
                    );
                  })()}
                </TableHead>
                <TableBody>
                  {rows.map((r, idx) => (
                    <TableRow
                      key={r.assessment_id}
                      hover
                      onClick={() => openDetail(r.assessment_id)}
                      sx={{
                        cursor: "pointer",
                        bgcolor: idx % 2 === 0 ? "#fff" : "#fafafa",
                        "&:hover": { bgcolor: "#f0f4ff" },
                      }}
                    >
                      <TableCell sx={{ fontSize: 12.5, fontWeight: 600, color: NAVY }}>{fmtDate(r.assessment_date)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <StudentAvatar name={r.student?.name ?? "—"} size={30} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1a1a2e" }} noWrap>
                              {r.student?.name ?? "—"}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>
                              {r.student?.grade ?? r.grade ?? "—"}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {r.subject
                          ? <Chip size="small" label={r.subject} sx={{ bgcolor: "#eff6ff", color: "#2563eb", fontWeight: 600, fontSize: 11, borderRadius: 1.5, height: 22 }} />
                          : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12.5, color: "#1a1a2e" }}>
                        <Box sx={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</Box>
                      </TableCell>
                      <TableCell>
                        {r.grade
                          ? <Chip size="small" label={r.grade} sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 600, fontSize: 11, borderRadius: 1.5, height: 22 }} />
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {r.marked
                          ? <Chip size="small" label="Graded" sx={{ bgcolor: "#d1fae5", color: "#065f46", fontWeight: 800, fontSize: 10.5, height: 22 }} />
                          : <Chip size="small" label="Needs Review" sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 800, fontSize: 10.5, height: 22 }} />}
                      </TableCell>
                      <TableCell>{renderResult(r)}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => openDetail(r.assessment_id)} sx={{ bgcolor: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: 1.5, mr: 0.5, "&:hover": { bgcolor: "#dbeafe" } }}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!r.marked && (
                          <Tooltip title="Grade / Review">
                            {/* Same page as View; the ?mode=grade query
                                param opens the grading panel in edit mode
                                and scrolls to it. Never a second URL. */}
                            <IconButton size="small" onClick={() => navigate(`/staff-portal/assessments/${r.assessment_id}?mode=grade`)} sx={{ bgcolor: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", borderRadius: 1.5, "&:hover": { bgcolor: "#dcfce7" } }}>
                              <HowToRegOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2, borderTop: "1px solid #f1f5f9" }}>
              <Pagination page={page} count={pageCount} onChange={(_, p) => setPage(p)} size="small" />
            </Box>
          )}
        </Card>
      </Box>

      <StaffAssessmentDialog
        open={dialogOpen}
        onClose={closeDialog}
        onCreated={onCreated}
        prefillStudentId={Number(searchParams.get("student_id")) || null}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToast(null)} sx={{ fontWeight: 700 }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ── Presentational helpers ──────────────────────────────────────

const KpiCard: React.FC<{ label: string; value: React.ReactNode; icon: React.ReactNode; accent: string; bg: string }> = ({
  label, value, icon, accent, bg,
}) => (
  <Box
    sx={{
      bgcolor: bg,
      borderRadius: "14px",
      borderTop: `3px solid ${accent}`,
      border: `1px solid ${alpha(accent, 0.16)}`,
      borderTopWidth: 3,
      p: 2,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      boxShadow: "0 1px 4px rgba(15,23,42,0.06)",
      transition: "transform 160ms ease-out, box-shadow 160ms ease-out",
      "&:hover": { transform: "translateY(-2px)", boxShadow: `0 6px 16px ${alpha(accent, 0.18)}` },
    }}
  >
    <Box
      sx={{
        width: 40, height: 40, borderRadius: "10px",
        bgcolor: alpha(accent, 0.16),
        color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.6px", opacity: 0.9 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1, color: "#0f172a", fontVariantNumeric: "tabular-nums", mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const StudentAvatar: React.FC<{ name: string; size?: number }> = ({ name, size = 34 }) => (
  <Box
    sx={{
      width: size, height: size, borderRadius: "50%",
      bgcolor: alpha(NAVY, 0.12), color: NAVY,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size <= 30 ? 11 : 12,
      fontWeight: 800, flexShrink: 0,
    }}
  >
    {initials(name) || "?"}
  </Box>
);

const renderResult = (r: Row) => {
  if (r.marked && r.percentage !== null) {
    const tone = percentTone(r.percentage);
    return <Chip size="small" label={`${Math.round(r.percentage)}%`} sx={{ bgcolor: tone.bg, color: tone.fg, fontWeight: 800, fontSize: 10.5, height: 22 }} />;
  }
  return <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>;
};

const EmptyState: React.FC<{ tab: TabKey; search: string; canCreate: boolean; onCreate: () => void }> = ({
  tab, search, canCreate, onCreate,
}) => {
  const headline =
    tab === "to_review" ? "All caught up. No assessments need your review."
    : search ? "No results match your filters."
    : "No assessments found";
  const body =
    tab === "to_review" ? "Create a new assessment for one of your assigned students, or switch to Graded / All to see history."
    : search ? "Try clearing the search box or switching tabs."
    : "Create your first assessment for a student assigned to you.";
  return (
    <Box sx={{ p: { xs: 4, md: 6 }, textAlign: "center" }}>
      <Box
        sx={{
          width: 60, height: 60, mx: "auto", mb: 1.5,
          borderRadius: "50%", bgcolor: alpha(NAVY, 0.08),
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 30, color: NAVY }} />
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 16, color: NAVY }}>{headline}</Typography>
      <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 0.75, maxWidth: 420, mx: "auto" }}>{body}</Typography>
      {canCreate && (
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={onCreate}
          sx={{
            mt: 2.5, textTransform: "none", fontWeight: 800, borderRadius: 2,
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_2} 100%)`,
            boxShadow: "0 4px 14px rgba(27,58,107,0.35)",
            "&:hover": { boxShadow: "0 6px 18px rgba(27,58,107,0.45)" },
            px: 3, minHeight: 42,
          }}
        >
          Create Assessment
        </Button>
      )}
    </Box>
  );
};

export default Assessments;
